import { supabase } from "@/lib/supabase";
import { dateOlderThanSeconds } from "./utils-functions";
import { useCentralStore } from "../../store/Central";
import { useDesktopsStore } from "../../store/Desktops";
import { Logger } from "@/lib/logger/Logger";
import { showNotification } from "../../store/Central/selectors";
import { DesktopSystemRequestTypes } from "../../store/Desktops/types";
import {
	PrinterConfigEntityType,
	DesktopEntityTypeWithSystems,
} from "@/lib/supabase/supabaseTypes";

interface PrintJobRequest {
	documentType: string;
	documentData: Blob;
	fileName: string;
}

interface PrintJobResult {
	success: boolean;
	requestId: number | null;
	desktopOnline: boolean;
}

function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			const base64 = (reader.result as string).split(",")[1];
			resolve(base64);
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

export async function sendPrintJob(
	request: PrintJobRequest
): Promise<PrintJobResult> {
	const { profile } = useCentralStore.getState();
	const { locations, printerConfigs, desktopsWithSystems } =
		useDesktopsStore.getState();

	const resolvedLocationId =
		profile?.location_id ?? locations.find((l) => l.is_default)?.id ?? null;

	if (!resolvedLocationId) {
		showNotification({
			message: "Kein Standort konfiguriert",
			type: "error",
		});
		return { success: false, requestId: null, desktopOnline: false };
	}

	let usedFallback = false;
	let config = printerConfigs.find(
		(c) =>
			c.location_id === resolvedLocationId &&
			c.config_key === request.documentType
	);
	if (!config || !config.desktop_system_id) {
		const fallback = printerConfigs.find(
			(c) =>
				c.location_id === resolvedLocationId &&
				c.config_key === "default"
		);
		if (fallback?.desktop_system_id) {
			config = fallback;
			usedFallback = true;
		}
	}
	if (!config || !config.desktop_system_id) {
		showNotification({
			message: "Kein Drucker für diesen Standort konfiguriert",
			type: "error",
		});
		return { success: false, requestId: null, desktopOnline: false };
	}
	if (usedFallback) {
		showNotification({
			message:
				"Kein Drucker für diesen Dokumenttyp konfiguriert — Standarddrucker wird verwendet",
			type: "info",
		});
	}

	let targetDesktopId: string | null = null;

	for (const desktop of desktopsWithSystems) {
		const hasSystem = desktop.desktop_systems.some(
			(s) => s.id === config!.desktop_system_id
		);
		if (hasSystem) {
			targetDesktopId = desktop.id;
			break;
		}
	}

	if (!targetDesktopId) {
		showNotification({
			message: "Desktop für den konfigurierten Drucker nicht gefunden",
			type: "error",
		});
		return { success: false, requestId: null, desktopOnline: false };
	}

	const { data: freshDesktop } = await supabase
		.from("desktops")
		.select("last_active_at")
		.eq("id", targetDesktopId)
		.single();

	const desktopOnline = freshDesktop
		? !dateOlderThanSeconds(new Date(freshDesktop.last_active_at), 60)
		: false;

	const base64Data = await blobToBase64(request.documentData);

	const printerSystem = desktopsWithSystems
		.flatMap((d) => d.desktop_systems)
		.find((s) => s.id === config!.desktop_system_id);

	const requestData = {
		RequestType: DesktopSystemRequestTypes.PrintDocument,
		FileType: "pdf",
		PrinterName: printerSystem?.name ?? "",
		TrayNumber: config.tray_number,
		PaperSize: config.paper_size,
		Duplex: config.duplex,
		ColorMode: config.color_mode,
		Copies: config.copies,
		PagesPerSheet: config.pages_per_sheet,
		MarginMm: config.margin_mm,
		ScalePercent: config.scale_percent,
		CompatMode: printerSystem?.compat_mode ?? false,
		FileName: request.fileName.endsWith(".pdf")
			? request.fileName
			: `${request.fileName}.pdf`,
		Data: base64Data,
	};

	const { data, error } = await supabase
		.from("desktop_jobs")
		.insert({
			type: DesktopSystemRequestTypes.PrintDocument,
			data: requestData,
			desktop_id: targetDesktopId,
			desktop_system_id: config.desktop_system_id,
		})
		.select("id")
		.single();

	if (error) {
		Logger.error(error);
		showNotification({
			message: "Fehler beim Erstellen des Druckauftrags",
			type: "error",
		});
		return { success: false, requestId: null, desktopOnline: false };
	}

	const orgId = useCentralStore.getState().organization?.id;
	if (orgId) useDesktopsStore.getState().fetchPrintJobs(orgId);

	return {
		success: true,
		requestId: data?.id ?? null,
		desktopOnline,
	};
}

export enum PrintResponseStatus {
	Success = 0,
	GeneralError = 1,
	PrinterNotAccessible = 2,
	PrinterOffline = 3,
	PaperJam = 4,
	OutOfPaper = 5,
	ManualFeed = 6,
	PaperProblem = 7,
	LabelCountZero = 8,
}

export const PRINT_STATUS_MESSAGES: Record<number, string> = {
	[PrintResponseStatus.Success]: "Dokument erfolgreich an Drucker gesendet",
	[PrintResponseStatus.GeneralError]: "Allgemeiner Druckfehler",
	[PrintResponseStatus.PrinterNotAccessible]: "Drucker nicht erreichbar",
	[PrintResponseStatus.PrinterOffline]: "Drucker ist offline",
	[PrintResponseStatus.PaperJam]: "Papierstau",
	[PrintResponseStatus.OutOfPaper]: "Kein Papier",
	[PrintResponseStatus.ManualFeed]: "Manueller Papiereinzug erforderlich",
	[PrintResponseStatus.PaperProblem]: "Papierproblem",
	[PrintResponseStatus.LabelCountZero]: "Keine Etiketten konfiguriert",
};

const MINIMAL_TEST_PDF_BASE64 =
	"JVBERi0xLjQKMSAwIG9iago8PCAvVHlwZSAvQ2F0YWxvZyAvUGFnZXMgMiAwIFIgPj4KZW5kb2Jq" +
	"CjIgMCBvYmoKPDwgL1R5cGUgL1BhZ2VzIC9LaWRzIFszIDAgUl0gL0NvdW50IDEgPj4KZW5kb2Jq" +
	"CjMgMCBvYmoKPDwgL1R5cGUgL1BhZ2UgL1BhcmVudCAyIDAgUiAvTWVkaWFCb3ggWzAgMCA2MTIg" +
	"NzkyXSAvQ29udGVudHMgNCAwIFIgL1Jlc291cmNlcyA8PCAvRm9udCA8PCAvRjEgNSAwIFIgPj4g" +
	"Pj4gPj4KZW5kb2JqCjQgMCBvYmoKPDwgL0xlbmd0aCA0NCA+PgpzdHJlYW0KQlQgL0YxIDEyIFRm" +
	"IDEwMCA3MDAgVGQgKFRlc3RkcnVjaykgVGogRVQKZW5kc3RyZWFtCmVuZG9iago1IDAgb2JqCjw8" +
	"IC9UeXBlIC9Gb250IC9TdWJ0eXBlIC9UeXBlMSAvQmFzZUZvbnQgL0hlbHZldGljYSA+PgplbmRv" +
	"YmoKeHJlZgowIDYKMDAwMDAwMDAwMCA2NTUzNSBmIAowMDAwMDAwMDA5IDAwMDAwIG4gCjAwMDAw" +
	"MDAwNTggMDAwMDAgbiAKMDAwMDAwMDExNSAwMDAwMCBuIAowMDAwMDAwMjQxIDAwMDAwIG4gCjAw" +
	"MDAwMDAzMzIgMDAwMDAgbiAKdHJhaWxlcgo8PCAvU2l6ZSA2IC9Sb290IDEgMCBSID4+CnN0YXJ0" +
	"eHJlZgo0MDIKJSVFT0YK";

export type TestPrintConfig = Pick<
	PrinterConfigEntityType,
	"desktop_system_id" | "tray_number" | "paper_size" | "duplex" | "color_mode"
>;

export async function sendTestPrintJob(
	config: TestPrintConfig,
	desktopsWithSystems: DesktopEntityTypeWithSystems[],
	testDocumentBlob?: Blob
): Promise<PrintJobResult> {
	if (!config.desktop_system_id) {
		showNotification({
			message: "Kein Drucker konfiguriert",
			type: "error",
		});
		return { success: false, requestId: null, desktopOnline: false };
	}

	let targetDesktopId: string | null = null;
	let desktopOnline = false;

	for (const desktop of desktopsWithSystems) {
		const hasSystem = desktop.desktop_systems.some(
			(s) => s.id === config.desktop_system_id
		);
		if (hasSystem) {
			targetDesktopId = desktop.id;
			desktopOnline = !dateOlderThanSeconds(
				new Date(desktop.last_active_at),
				60
			);
			break;
		}
	}

	if (!targetDesktopId) {
		showNotification({
			message: "Desktop für den konfigurierten Drucker nicht gefunden",
			type: "error",
		});
		return { success: false, requestId: null, desktopOnline: false };
	}

	const printerSystem = desktopsWithSystems
		.flatMap((d) => d.desktop_systems)
		.find((s) => s.id === config.desktop_system_id);

	const base64Data = testDocumentBlob
		? await blobToBase64(testDocumentBlob)
		: MINIMAL_TEST_PDF_BASE64;

	const requestData = {
		RequestType: DesktopSystemRequestTypes.PrintDocument,
		FileType: "pdf",
		PrinterName: printerSystem?.name ?? "",
		TrayNumber: config.tray_number,
		PaperSize: config.paper_size,
		Duplex: config.duplex,
		ColorMode: config.color_mode,
		CompatMode: printerSystem?.compat_mode ?? false,
		FileName: "testdruck.pdf",
		Data: base64Data,
	};

	const { data, error } = await supabase
		.from("desktop_jobs")
		.insert({
			type: DesktopSystemRequestTypes.PrintDocument,
			data: requestData,
			desktop_id: targetDesktopId,
			desktop_system_id: config.desktop_system_id,
		})
		.select("id")
		.single();

	if (error) {
		Logger.error(error);
		showNotification({
			message: "Fehler beim Erstellen des Testdrucks",
			type: "error",
		});
		return { success: false, requestId: null, desktopOnline: false };
	}

	const orgId = useCentralStore.getState().organization?.id;
	if (orgId) useDesktopsStore.getState().fetchPrintJobs(orgId);

	return {
		success: true,
		requestId: data?.id ?? null,
		desktopOnline,
	};
}

export async function pollPrintResponse(jobId: number): Promise<void> {
	const POLL_INTERVAL = 5000;
	const POLL_TIMEOUT = 60000;

	const handleResult = (data: {
		response_status: number | null;
		response_message: string | null;
	}) => {
		const message =
			data.response_message ??
			PRINT_STATUS_MESSAGES[data.response_status ?? -1] ??
			"Unbekannter Druckstatus";

		if (data.response_status === PrintResponseStatus.Success) {
			showNotification({ message, type: "success" });
		} else {
			showNotification({
				message,
				type: "error",
				duration: 10000,
				action: {
					label: "Erneut versuchen",
					onClick: () => {
						const job = useDesktopsStore
							.getState()
							.printJobs.find((j) => j.id === jobId);
						if (job) useDesktopsStore.getState().retryPrintJob(job);
					},
				},
			});
		}
	};

	const isTerminal = (status: string) =>
		status !== "pending" && status !== "processing";

	return new Promise<void>((resolve) => {
		let settled = false;
		let fallbackTimer: ReturnType<typeof setTimeout>;
		let pollTimer: ReturnType<typeof setInterval>;

		const cleanup = () => {
			if (settled) return;
			settled = true;
			clearTimeout(fallbackTimer);
			clearInterval(pollTimer);
			channel.unsubscribe();
			const orgId = useCentralStore.getState().organization?.id;
			if (orgId) useDesktopsStore.getState().fetchPrintJobs(orgId);
			resolve();
		};

		const channel = supabase
			.channel(`print-response:${jobId}`)
			.on(
				"postgres_changes",
				{
					event: "UPDATE",
					schema: "public",
					table: "desktop_jobs",
					filter: `id=eq.${jobId}`,
				},
				(payload) => {
					const row = payload.new as {
						status: string;
						response_status: number | null;
						response_message: string | null;
					};
					if (isTerminal(row.status)) {
						handleResult(row);
						cleanup();
					}
				}
			)
			.subscribe();

		const poll = async () => {
			if (settled) return;
			const { data, error } = await supabase
				.from("desktop_jobs")
				.select("status, response_status, response_message")
				.eq("id", jobId)
				.single();

			if (error) {
				Logger.error(error);
				return;
			}
			if (data && isTerminal(data.status)) {
				handleResult(data);
				cleanup();
			}
		};

		poll();
		pollTimer = setInterval(poll, POLL_INTERVAL);

		fallbackTimer = setTimeout(() => {
			if (!settled) {
				showNotification({
					message:
						"Druckantwort nicht erhalten — Desktop antwortet nicht",
					type: "warning",
				});
				cleanup();
			}
		}, POLL_TIMEOUT);
	});
}
